import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Input,
  Select,
  Space,
  Card,
  Row,
  Col,
  Divider,
  message
} from 'antd'
import { RobotOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import {
  getRequest,
  postRequest,
  patchRequest,
  dutyBroadcastsUrl,
  dutyBroadcastDetailUrl,
  dutyBroadcastConfigUrl,
  dutyBroadcastAiDraftUrl,
  dutyPromotionsUrl
} from '../../../config'

const TONES = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'professional', label: 'Professional' },
  { value: 'celebratory', label: 'Celebratory' },
  { value: 'concise', label: 'Concise' }
]

// Step 1 of a broadcast: name + message body. The message must exist before picking recipients,
// so this is the entry point. Supports manual writing/pasting and an AI-assist panel that returns
// several options to choose from. Editing an existing draft when `:id` is present.
const BroadcastCompose = props => {
  const editingId = props.match.params.id || null
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [body, setBody] = useState('')

  // Optional promotion this broadcast advertises (spec: promotions). When set, the AI draft can
  // reference the real offer.
  const [promotions, setPromotions] = useState([])
  const [promotionId, setPromotionId] = useState(null)

  // The fixed heading/sign-off that wraps every message (mirrors the WhatsApp template), fetched
  // so the compose screen can preview exactly what customers receive.
  const [frame, setFrame] = useState({ textHeader: '', textFooter: '' })

  // AI panel state.
  const [aiOpen, setAiOpen] = useState(false)
  const [description, setDescription] = useState('')
  const [tone, setTone] = useState('friendly')
  const [count, setCount] = useState(3)
  const [generating, setGenerating] = useState(false)
  const [drafts, setDrafts] = useState([])

  useEffect(() => {
    const loadFrame = async () => {
      const res = await getRequest(dutyBroadcastConfigUrl, 'broadcast config', () => {})
      if (res.success) {
        setFrame({
          textHeader: res.data.textHeader || '',
          textFooter: res.data.textFooter || ''
        })
      }
    }
    loadFrame()
    const loadPromotions = async () => {
      const res = await getRequest(dutyPromotionsUrl, 'promotions', () => {})
      if (res.success) setPromotions(res.data.results || res.data || [])
    }
    loadPromotions()
  }, [])

  useEffect(() => {
    if (!editingId) return
    const load = async () => {
      const res = await getRequest(
        dutyBroadcastDetailUrl(editingId),
        'broadcast',
        setLoading
      )
      if (res.success) {
        setName(res.data.name || '')
        setBody(res.data.body || '')
        setPromotionId(res.data.promotion || null)
      }
    }
    load()
    // eslint-disable-next-line
  }, [editingId])

  const selectedPromo = promotions.find(p => p.id === promotionId) || null

  const generate = async () => {
    if (!description.trim()) {
      message.warning('Describe what the message should say first.')
      return
    }
    const promoCtx = selectedPromo
      ? {
          promotionPercent: selectedPromo.discountPercent,
          promotionEnd: selectedPromo.endAt
            ? new Date(selectedPromo.endAt).toLocaleDateString()
            : ''
        }
      : {}
    const res = await postRequest(
      dutyBroadcastAiDraftUrl,
      { description, tone, count, ...promoCtx },
      'AI drafts',
      setGenerating
    )
    if (res.success) {
      setDrafts(res.data.drafts || [])
    } else {
      // Fail-soft: manual composition always remains available.
      message.error('AI drafting is unavailable right now — you can still write it yourself.')
    }
  }

  const saveAndContinue = async () => {
    if (!name.trim()) {
      message.warning('Give the broadcast a name.')
      return
    }
    if (!body.trim()) {
      message.warning('The message body is required before choosing recipients.')
      return
    }
    const payload = { name, body, promotion: promotionId || null }
    const res = editingId
      ? await patchRequest(
          dutyBroadcastDetailUrl(editingId),
          payload,
          'broadcast',
          setLoading
        )
      : await postRequest(dutyBroadcastsUrl, payload, 'broadcast', setLoading)
    if (res.success) {
      const id = res.data.id
      props.history.push(`/settings/broadcasts/${id}/recipients`)
    }
  }

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <Space style={{ marginBottom: 8 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => props.history.push('/settings/broadcasts')}
            >
              Back
            </Button>
          </Space>
          <h3 style={{ marginBottom: 4 }}>
            {editingId ? 'Edit broadcast' : 'New broadcast'} — compose the message
          </h3>
          <p style={{ color: '#888', marginBottom: 16 }}>
            Write or paste the message, or let AI draft a few options. You&apos;ll pick who to
            send to on the next step.
          </p>

          <Row gutter={16}>
            <Col xs={24} md={14}>
              <label style={{ fontWeight: 600 }}>Name</label>
              <Input
                placeholder="Internal name, e.g. July discount"
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              <label style={{ fontWeight: 600 }}>Promotion (optional)</label>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Link a promotion this broadcast advertises"
                style={{ width: '100%', marginBottom: 4 }}
                value={promotionId || undefined}
                onChange={v => setPromotionId(v || null)}
                options={promotions.map(p => ({
                  value: p.id,
                  label: `${p.name} — ${p.discountPercent}%${p.isActive ? '' : ' (off)'}`
                }))}
              />
              {selectedPromo ? (
                <p style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
                  AI drafts can reference {selectedPromo.discountPercent}% off
                  {selectedPromo.endAt
                    ? ` until ${new Date(selectedPromo.endAt).toLocaleDateString()}`
                    : ''}
                  .
                </p>
              ) : (
                <div style={{ marginBottom: 12 }} />
              )}
              <label style={{ fontWeight: 600 }}>Message</label>
              <Input.TextArea
                rows={9}
                placeholder="Type or paste the message content — greeting, offer, and a reply-based call to action. Don't add a title or sign-off; those are added automatically."
                value={body}
                onChange={e => setBody(e.target.value)}
                maxLength={4000}
                showCount
              />

              {/* Live preview of exactly what customers receive — the fixed heading + sign-off wrap
                  the composed content (identical for in-window and templated out-of-window sends). */}
              {frame.textHeader || frame.textFooter ? (
                <Card
                  size="small"
                  title="Preview — what customers receive"
                  style={{ marginTop: 12, background: 'rgba(0,0,0,0.02)' }}
                  bodyStyle={{ whiteSpace: 'pre-wrap' }}
                >
                  {[frame.textHeader, body || '…', frame.textFooter]
                    .filter(Boolean)
                    .join('\n\n')}
                </Card>
              ) : null}

              <Space style={{ marginTop: 16 }}>
                <Button type="primary" onClick={saveAndContinue}>
                  Save &amp; choose recipients
                </Button>
                <Button
                  icon={<RobotOutlined />}
                  onClick={() => setAiOpen(o => !o)}
                >
                  {aiOpen ? 'Hide AI assist' : 'AI assist'}
                </Button>
              </Space>
            </Col>

            {aiOpen ? (
              <Col xs={24} md={10}>
                <Card size="small" title="Draft with AI" style={{ marginTop: 20 }}>
                  <label>What should the message say?</label>
                  <Input.TextArea
                    rows={3}
                    placeholder="e.g. Announce a 10% July discount on customs-duty service for returning customers"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  <Space wrap style={{ marginBottom: 8 }}>
                    <Select
                      style={{ width: 150 }}
                      value={tone}
                      onChange={setTone}
                      options={TONES}
                    />
                    <Select
                      style={{ width: 120 }}
                      value={count}
                      onChange={setCount}
                      options={[1, 2, 3, 4, 5].map(n => ({
                        value: n,
                        label: `${n} idea${n > 1 ? 's' : ''}`
                      }))}
                    />
                    <Button type="primary" loading={generating} onClick={generate}>
                      Generate
                    </Button>
                  </Space>

                  {drafts.length ? <Divider style={{ margin: '8px 0' }} /> : null}
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {drafts.map((d, i) => (
                      <Card
                        key={i}
                        size="small"
                        bodyStyle={{ padding: 10 }}
                        style={{ background: 'rgba(0,0,0,0.02)' }}
                      >
                        <div style={{ whiteSpace: 'pre-wrap', marginBottom: 8 }}>{d}</div>
                        <Button
                          size="small"
                          type="link"
                          style={{ padding: 0 }}
                          onClick={() => {
                            setBody(d)
                            message.success('Draft copied into the message.')
                          }}
                        >
                          Use this
                        </Button>
                      </Card>
                    ))}
                  </Space>
                </Card>
              </Col>
            ) : null}
          </Row>
        </div>
      </div>
    </PageLoader>
  )
}

BroadcastCompose.propTypes = {
  history: PropTypes.object,
  match: PropTypes.object
}

export default BroadcastCompose
